/**
 * ForceAssignModal — force-assign a rejected job back to a driver.
 *
 * Phase 9 / Plan 09-04 — shadcn Dialog implementing UI-SPEC §7 (DISPATCH-05).
 * Available only for DISPATCH_MANAGER-tier users (UX gate; server enforces authz).
 *
 * - Title: "Force Assign Job [HaulId]" (EXACT UI-SPEC copy)
 * - Driver selector: searchable Command/Combobox from GET /v1/drivers (FULL roster, Blocker 4)
 * - Reason field: required Textarea (3 rows)
 * - Primary CTA: "Force Assign" (accent-blue) — disabled until driver + reason selected
 * - Secondary CTA: "Keep Assignment" — closes without mutation
 *
 * POST /v1/jobs/bulk-assign with { jobIds: [job.id], targetDriverId, note: reason }
 * — the single REJECTED jobId on the elevated path (server allows REJECTED→PENDING
 * for DISPATCH_MANAGER+).
 *
 * On success: invalidate jobs query (badge clears + status updates), close modal, toast.
 * On error: surface failure message without losing form state.
 *
 * Spec: UI-SPEC §7 (force-assign-back); Copywriting; DISPATCH-05; T-09-04-06.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ChevronsUpDown } from 'lucide-react';
import { apiErrorText, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@lodex-pro/web-shared/src/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@lodex-pro/web-shared/src/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@lodex-pro/web-shared/src/components/ui/popover';
import { Button } from '@lodex-pro/web-shared/src/components/ui/button';
import { Textarea } from '@lodex-pro/web-shared/src/components/ui/textarea';
import { useDrivers } from './use-drivers';
import { JOBS_QUERY_KEY } from './use-jobs';
import { OperatorRequestNote } from './OperatorRequestNote';

interface BulkAssignBody {
  readonly jobIds: readonly string[];
  readonly targetDriverId: string;
  readonly note?: string;
}

interface BulkAssignResponse {
  readonly ok: boolean;
  readonly assigned: number;
  readonly failed: ReadonlyArray<{ readonly id: string; readonly reason: string }>;
}

export interface ForceAssignModalProps {
  /** The rejected job to re-assign. */
  readonly job: {
    readonly id: string;
    readonly haulId: string;
    /**
     * DEC-299 / Task 18.6 — the operator's note on an API haul request. The modal
     * covers the queue row that shows it, so it is repeated here, where the driver is
     * chosen. Optional: absent for every non-API job.
     */
    readonly operatorRequestNotes?: string | null;
  };
  /** Whether the dialog is open. */
  readonly open: boolean;
  /** Called when the open state should change. */
  readonly onOpenChange: (open: boolean) => void;
}

export function ForceAssignModal({
  job,
  open,
  onOpenChange,
}: ForceAssignModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();

  const { data: driversData } = useDrivers(apiOpts);
  const drivers = React.useMemo(() => driversData?.drivers ?? [], [driversData]);

  const [driverPickerOpen, setDriverPickerOpen] = React.useState(false);
  const [selectedDriverId, setSelectedDriverId] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const selectedDriverName = React.useMemo(() => {
    if (selectedDriverId === null) return null;
    return drivers.find((d) => d.driverId === selectedDriverId)?.driverName ?? null;
  }, [selectedDriverId, drivers]);

  const canSubmit = selectedDriverId !== null && reason.trim().length > 0;

  const mutation = useApiMutation<BulkAssignBody, BulkAssignResponse>(
    apiOpts,
    '/v1/jobs/bulk-assign',
    'POST',
    {
      onSuccess: (result) => {
        if (result.assigned > 0) {
          toast.success(`Job ${job.haulId} force-assigned successfully.`);
          void queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY({}) });
          onOpenChange(false);
        } else {
          // The server returned 200 but the job ended up in `failed` — surface the reason
          const failReason = result.failed[0]?.reason ?? 'unknown error';
          setErrorMessage(`Could not force-assign: ${failReason}`);
        }
      },
      onError: (err) => {
        setErrorMessage(apiErrorText(err, 'Force assign failed. Please try again.'));
      },
    },
  );

  const handleForceAssign = (): void => {
    if (selectedDriverId === null || reason.trim().length === 0) return;
    setErrorMessage(null);
    mutation.mutate({
      jobIds: [job.id],
      targetDriverId: selectedDriverId,
      note: reason.trim(),
    });
  };

  const handleKeepAssignment = (): void => {
    onOpenChange(false);
  };

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedDriverId(null);
      setReason('');
      setErrorMessage(null);
      setDriverPickerOpen(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="[--dialog-bg:var(--color-bg-secondary)]"
        style={{
          border: '1px solid var(--color-border-primary)',
          color: 'var(--color-text-primary)',
          maxWidth: '480px',
        }}
      >
        <DialogHeader>
          {/* EXACT UI-SPEC title: "Force Assign Job [HaulId]" */}
          <DialogTitle
            style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-primary)' }}
          >
            Force Assign Job {job.haulId}
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
          <OperatorRequestNote notes={job.operatorRequestNotes} testId="fam-operator-note" />
          {/* Driver selector — sourced from GET /v1/drivers (FULL roster, Blocker 4) */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px',
              }}
            >
              Driver <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <Popover open={driverPickerOpen} onOpenChange={setDriverPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={driverPickerOpen}
                  aria-label="Select driver"
                  style={{
                    width: '100%',
                    justifyContent: 'space-between',
                    backgroundColor: 'var(--color-bg-tertiary)',
                    borderColor: 'var(--color-border-secondary)',
                    color:
                      selectedDriverName !== null
                        ? 'var(--color-text-primary)'
                        : 'var(--color-text-tertiary)',
                  }}
                >
                  {selectedDriverName ?? 'Select a driver…'}
                  <ChevronsUpDown size={14} aria-hidden="true" style={{ marginLeft: '4px' }} />
                </Button>
              </PopoverTrigger>
              <PopoverContent style={{ width: '100%', padding: 0 }}>
                <Command>
                  <CommandInput placeholder="Search drivers…" />
                  <CommandList>
                    <CommandEmpty>No drivers found.</CommandEmpty>
                    <CommandGroup>
                      {drivers.map((driver) => (
                        <CommandItem
                          key={driver.driverId}
                          value={driver.driverName}
                          onSelect={(): void => {
                            setSelectedDriverId(driver.driverId);
                            setDriverPickerOpen(false);
                          }}
                        >
                          <Check
                            size={14}
                            aria-hidden="true"
                            style={{
                              marginRight: '8px',
                              opacity: selectedDriverId === driver.driverId ? 1 : 0,
                            }}
                          />
                          {driver.driverName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reason field — required, 3 rows */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px',
              }}
            >
              Reason for reassignment <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e): void => {
                setReason(e.target.value);
              }}
              placeholder="Explain why this job is being force-assigned…"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                borderColor: 'var(--color-border-secondary)',
                color: 'var(--color-text-primary)',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Error message */}
          {errorMessage !== null && (
            <p style={{ fontSize: '14px', color: 'var(--color-danger)', margin: 0 }}>
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {/* Secondary CTA — "Keep Assignment" (EXACT UI-SPEC copy) */}
          <Button variant="ghost" onClick={handleKeepAssignment} disabled={mutation.isPending}>
            Keep Assignment
          </Button>

          {/* Primary CTA — "Force Assign" (EXACT UI-SPEC copy, accent-blue) */}
          <Button
            onClick={handleForceAssign}
            disabled={!canSubmit || mutation.isPending}
            style={{
              backgroundColor:
                canSubmit && !mutation.isPending ? 'var(--color-accent-blue)' : undefined,
              color: canSubmit && !mutation.isPending ? 'var(--color-on-accent)' : undefined,
            }}
          >
            {mutation.isPending ? 'Assigning…' : 'Force Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
